import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { PenLine } from "lucide-react";
import { fromJS } from "immutable";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getBillingAddress, updateBillingAddress } from "../../api/BillingApi";
import LayoutAction from "../../actions/LayoutAction";
import InputInline from "../../components/InputInline";

const BillingAddress = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    country: "",
    postalCode: "",
    state: "",
  });

  const billingAddressQuery = useQuery({
    queryKey: ["billing-address"],
    queryFn: () => getBillingAddress(),
  });

  const billingAddress = fromJS(billingAddressQuery.data || {});

  const updateBillingAddressMutation = useMutation({
    mutationFn: async (params: any) => {
      return await updateBillingAddress(params);
    },
    onSuccess: () => {
      dispatch(
        LayoutAction.setNotification({
          title: t("Billing Address"),
          type: "success",
          message: t("Updated billing address!"),
        })
      );
      setIsEdit(false);
      billingAddressQuery.refetch();
    },
    onError: () => {
      dispatch(
        LayoutAction.setNotification({
          title: t("Billing Address"),
          type: "error",
          message: t("Failed to update billing address"),
        })
      );
    },
  });

  useEffect(() => {
    if (billingAddressQuery.data) {
      setFormData({
        ...billingAddressQuery.data,
      });
    }
  }, [billingAddressQuery.data]);

  const handleChangeField = (fieldId, fieldValue) => {
    setFormData({
      ...formData,
      [fieldId]: fieldValue,
    });
  };

  const handleSave = async () => {
    if (
      !formData.addressLine1 ||
      !formData.city ||
      !formData.country ||
      !formData.state ||
      !formData.postalCode
    ) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Billing Address"),
          type: "warning",
          message: t("Billing address required fields"),
        })
      );
      return;
    }
    updateBillingAddressMutation.mutate({
      ...billingAddress.toJS(),
      ...formData,
    });
  };

  const Row = ({ label, value, field, required = false }: any) => {
    const labelNode = (
      <div className="w-[120px] text-[12px] text-gray-900 leading-4 font-semibold">
        {label}
        {required ? <span className="text-gray-900">*</span> : null}
      </div>
    );

    const readNode = (
      <div className="flex-1 text-[12px] text-gray-600 leading-4 px-2 py-1.5">{value}</div>
    );

    const editNode = (
      <div className="flex-1">
        <InputInline
          border
          placeholder={t("Type {{field}}...", {
            field: String(label).toLowerCase(),
          })}
          value={formData[field] || ""}
          onFinish={(v) => handleChangeField(field, v)}
          error={required && !formData[field]}
        />
      </div>
    );

    return (
      <div className="flex items-start gap-1">
        {labelNode}
        {isEdit ? editNode : readNode}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-[12px]">
      {/* Header */}
      <div className="flex items-start justify-between p-3">
        <div>
          <div className="text-[12px] font-semibold text-gray-900 leading-4">{t("Address")}</div>
          <div className="text-[10px] font-medium text-gray-500 leading-4">
            {t("Update your billing address")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEdit ? (
            <button
              onClick={() => setIsEdit(true)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-gray-200 bg-white text-[10px] font-medium text-gray-800 hover:bg-gray-50"
            >
              <PenLine className="w-3.5 h-3.5" />
              {t("Edit")}
            </button>
          ) : (
            <div className="inline-flex items-center gap-2">
              <button
                onClick={() => setIsEdit(false)}
                className="px-2 py-1 rounded-md border border-gray-200 bg-white text-[12px] font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={updateBillingAddressMutation.isPending}
                className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-500 disabled:opacity-60"
              >
                {updateBillingAddressMutation.isPending ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/50 border-t-white rounded-full animate-spin inline-block" />
                ) : null}
                {t("Save")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-3 pb-3 space-y-2">
        <Row
          label={t("Address Line 1")}
          value={billingAddress.get("addressLine1")}
          field="addressLine1"
          required
        />
        <Row
          label={t("Address Line 2")}
          value={billingAddress.get("addressLine2")}
          field="addressLine2"
        />
        <Row label={t("City")} value={billingAddress.get("city")} field="city" required />
        <Row label={t("Country")} value={billingAddress.get("country")} field="country" required />
        <Row label={t("State")} value={billingAddress.get("state")} field="state" required />
        <Row
          label={t("Postal Code")}
          value={billingAddress.get("postalCode")}
          field="postalCode"
          required
        />
      </div>
    </div>
  );
};

export default BillingAddress;
